import { useCallback } from 'react';

import { useMutation } from '@apollo/client';

import type {
  GradedPlugin_SetLearnerPluginGradeMutation as Mutation,
  GradedPlugin_SetLearnerPluginGradeMutationVariables as Variables,
} from 'bundles/widget/queries/__generated__/setLearnerPluginGrade';
import SetLearnerPluginGradeMutation from 'bundles/widget/queries/setLearnerPluginGrade.graphql';

type SetGradeResult = {
  courseId: string;
  finalGrade: number;
  itemId: string;
  widgetSessionId: string;
};

export type SetGradeFnParams = {
  rawScore: number;
  minScore: number;
  maxScore: number;
  widgetSessionId: string;
  courseId: string;
  courseBranchId: string;
  itemId: string;
};

export const useSetGradedWidgetGrade = () => {
  const [mutate, { loading, error }] = useMutation<Mutation, Variables>(SetLearnerPluginGradeMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const setGrade = useCallback(
    async ({
      rawScore,
      minScore,
      maxScore,
      widgetSessionId,
      courseId,
      courseBranchId,
      itemId,
    }: SetGradeFnParams): Promise<SetGradeResult> => {
      // Calculate grade as percentage (0-1)
      const grade = maxScore > minScore ? (rawScore - minScore) / (maxScore - minScore) : 0;

      const result = await mutate({
        variables: {
          input: {
            courseId,
            courseBranchId,
            itemId,
            grade,
            widgetSessionId,
          },
        },
      });

      const data = result.data?.GradedPlugin_SetLearnerPluginGrade;

      if (data?.__typename === 'GradedPlugin_SetLearnerPluginGradeSuccess') {
        return {
          courseId: data.courseId,
          finalGrade: data.finalGrade,
          itemId: data.itemId,
          widgetSessionId: data.widgetSessionId,
        };
      }

      if (data?.__typename === 'GradedPlugin_SetLearnerPluginGradeFailure') {
        const errorMessages = data.errors?.map((err) => err.message).join(', ') || 'Mutation failed';
        throw new Error(errorMessages);
      }

      throw new Error('Unknown error occurred');
    },
    [mutate]
  );

  return {
    setGrade,
    loading,
    error,
  };
};
